import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { brand } from "../mock";
import { Printer, MessageCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Invoice = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/bookings/${id}/invoice`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.detail || "Invoice not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-secondary/30"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (error) return <div className="min-h-screen flex flex-col items-center justify-center bg-secondary/30 text-center p-6"><p className="text-lg font-semibold text-gray-800">{error}</p><p className="text-sm text-gray-500 mt-2">Please contact BeBlushify if you think this is a mistake.</p></div>;

  const b = data.booking;
  const dateStr = b.created_at ? new Date(b.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : b.date;
  const shareBill = () => {
    const phone = (b.phone || "").replace(/\D/g, "");
    const waPhone = phone.length === 10 ? `91${phone}` : phone;
    const msg = `My BeBlushify bill ${data.invoice_no} — Total ₹${b.total}. View: ${window.location.href}`;
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-secondary/30 py-8 px-4 print:bg-white print:py-0">
      <style>{`@media print { .no-print { display: none !important; } @page { margin: 12mm; } }`}</style>
      <div className="max-w-2xl mx-auto">
        <div className="no-print flex justify-end gap-2 mb-4">
          <button onClick={() => window.print()} data-testid="invoice-print-btn" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-5 h-10 text-sm font-semibold hover:bg-primary/90"><Printer className="w-4 h-4" /> Download / Print</button>
          <button onClick={shareBill} data-testid="invoice-share-btn" className="inline-flex items-center gap-2 bg-green-500 text-white rounded-full px-5 h-10 text-sm font-semibold hover:bg-green-600"><MessageCircle className="w-4 h-4" /> Share on WhatsApp</button>
        </div>

        <div className="bg-white rounded-2xl ring-1 ring-black/5 shadow-sm p-8 print:shadow-none print:ring-0 print:p-0" data-testid="invoice-card">
          <div className="flex items-start justify-between border-b pb-5">
            <div>
              <img src={brand.logo} alt={brand.name} className="h-12 w-auto object-contain" />
              <p className="text-sm text-gray-500 mt-2">{brand.address}</p>
              <p className="text-sm text-gray-500">{brand.phone} · {brand.email}</p>
            </div>
            <div className="text-right">
              <h1 className="font-display text-2xl font-bold text-gray-900">INVOICE</h1>
              <p className="text-sm text-gray-500 mt-1">{data.invoice_no}</p>
              <p className="text-sm text-gray-500">{dateStr}</p>
              <span className="inline-block mt-1 text-xs font-semibold bg-green-100 text-green-700 rounded-full px-2 py-0.5">PAID / COMPLETED</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 py-5 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Billed To</div>
              <div className="font-semibold text-gray-900">{b.customer_name}</div>
              <div className="text-gray-600">{b.phone}</div>
              <div className="text-gray-600">{b.city}{b.address ? `, ${b.address}` : ""}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Service Date</div>
              <div className="text-gray-900">{b.date || "—"} {b.time ? `· ${b.time}` : ""}</div>
              {b.assigned_beautician_name && <><div className="text-xs uppercase tracking-wide text-gray-400 mb-1 mt-2">Beautician</div><div className="text-gray-900">{b.assigned_beautician_name}</div></>}
            </div>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/50 text-left">
                <th className="py-2 px-3 rounded-l-lg font-semibold text-gray-700">Service</th>
                <th className="py-2 px-3 rounded-r-lg font-semibold text-gray-700 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(b.items || []).map((it, idx) => (
                <tr key={idx} className="border-b border-gray-100">
                  <td className="py-2.5 px-3 text-gray-800">{it.name}</td>
                  <td className="py-2.5 px-3 text-gray-800 text-right">₹{it.price}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end mt-4">
            <div className="w-full max-w-xs text-sm space-y-1.5">
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>₹{b.subtotal}</span></div>
              {b.discount > 0 && <div className="flex justify-between text-green-600"><span>Offer Discount</span><span>-₹{b.discount}</span></div>}
              {b.coins_redeemed > 0 && <div className="flex justify-between text-amber-600"><span>Coins Redeemed</span><span>-₹{b.coins_redeemed}</span></div>}
              <div className="flex justify-between font-bold text-gray-900 text-base border-t pt-2 mt-1"><span>Total</span><span>₹{b.total}</span></div>
              <div className="flex justify-between text-gray-500 text-xs"><span>Payment</span><span className="capitalize">{(b.payment_method || "pay_at_service").replace(/_/g, " ")}</span></div>
            </div>
          </div>

          <div className="border-t mt-6 pt-4 text-center text-xs text-gray-400">
            Thank you for choosing {brand.name}! 💖 · {brand.hours}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoice;
